import { gsap, SplitText } from '@/lib/gsap';

export const ENTRANCE_ROLES = {
	image: { opacity: 0, scale: 1.025, filter: 'blur(2px)' },
	title: { opacity: 0, y: 34, filter: 'blur(10px)' },
	text: { opacity: 0, y: 10, filter: 'blur(6px)' },
	control: { opacity: 0, y: 10, scale: 0.98, filter: 'blur(0px)' }
} satisfies Record<string, gsap.TweenVars>;

export type EntranceRole = keyof typeof ENTRANCE_ROLES;

const SETTLED: gsap.TweenVars = { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' };

export const prefersReducedMotion = () =>
	typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export interface EntranceStep {
	targets: Element | Element[] | NodeListOf<Element> | null | undefined;
	role?: EntranceRole;
	position?: gsap.Position;
	stagger?: number;
	duration?: number;
	from?: gsap.TweenVars;
	vars?: gsap.TweenVars;
	clearProps?: string | false;
}

const resolveTargets = (targets: EntranceStep['targets']): Element[] => {
	if (!targets) return [];

	if (Array.isArray(targets)) return targets;

	return targets instanceof Element ? [targets] : Array.from(targets);
};

export const settleEntrance = (
	targets: EntranceStep['targets'],
	clearProps: EntranceStep['clearProps'] = 'filter,transform'
) => {
	const elements = resolveTargets(targets);

	if (!elements.length) return;

	gsap.set(elements, clearProps === false ? SETTLED : { ...SETTLED, clearProps });
};

export const buildEntrance = (steps: EntranceStep[], timelineVars?: gsap.TimelineVars) => {
	const resolved = steps
		.map(step => ({ ...step, elements: resolveTargets(step.targets) }))
		.filter(step => step.elements.length > 0);

	if (!resolved.length) return gsap.timeline();

	if (prefersReducedMotion()) {
		resolved.forEach(step => settleEntrance(step.elements, step.clearProps));

		return gsap.timeline();
	}

	const timeline = gsap.timeline({
		defaults: { ease: 'power3.out', duration: 0.7 },
		...timelineVars
	});

	resolved.forEach(({ elements: stepElements, role = 'text', from, position, ...step }, index) => {
		gsap.set(stepElements, { ...ENTRANCE_ROLES[role], ...from });

		timeline.to(
			stepElements,
			{ ...SETTLED, stagger: step.stagger, duration: step.duration, ...step.vars },
			position ?? (index === 0 ? 0 : '<0.08')
		);
	});

	resolved.forEach(step => {
		if (step.clearProps === false) return;

		timeline.set(step.elements, { clearProps: step.clearProps ?? 'filter,transform' });
	});

	return timeline;
};

export interface TitleSplit {
	lines: Element[];
	revert: () => void;
}

const NO_SPLIT: TitleSplit = { lines: [], revert: () => {} };

export const splitTitleLines = (root: ParentNode, titleClass: string): TitleSplit => {
	const title = root.querySelector<HTMLElement>(`.${titleClass}`);

	if (!title) return NO_SPLIT;
	gsap.set(title, { opacity: 1 });

	if (prefersReducedMotion()) return { lines: [title], revert: () => {} };

	const split = new SplitText(title, { type: 'lines' });
	let reverted = false;

	return {
		lines: split.lines,
		revert: () => {
			if (reverted) return;

			reverted = true;
			split.revert();
		}
	};
};
